/**
 * Microphone capture into a rolling buffer.
 *
 * Pitch detection wants a few hundred milliseconds; beat detection wants
 * several seconds. Rather than run two capture paths, everything lands in one
 * ring buffer and each consumer takes the tail length it needs.
 */

import { audioContext, micAvailability, resumeAudio } from "./audio.js";
import { RingBuffer } from "./ring.js";

/**
 * Browser audio "enhancements" are tuned for speech and actively destroy a
 * steady tone: gain control breathes across the note, noise suppression treats
 * a held pipe as background hum and gates it, and echo cancellation will
 * subtract the app's own reference tone — which in beat mode is precisely the
 * signal being measured. All three off, always.
 */
const CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
    channelCount: 1,
  },
  video: false,
};

export class MicInput {
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private node: AudioWorkletNode | null = null;
  private sink: GainNode | null = null;
  private readonly ring = new RingBuffer(1);
  /** Bumped by stop() so a start() still in flight knows to abandon itself. */
  private generation = 0;

  /** Seconds of history retained. Sets the slowest measurable beat rate. */
  constructor(private readonly seconds = 8) {}

  get running(): boolean {
    return this.node !== null;
  }

  get sampleRate(): number {
    return audioContext().sampleRate;
  }

  get available(): number {
    return this.ring.available;
  }

  get totalCaptured(): number {
    return this.ring.totalWritten;
  }

  async start(): Promise<void> {
    if (this.node) return;

    const availability = micAvailability();
    if (!availability.ok) throw new Error(availability.reason);

    const generation = this.generation;
    await resumeAudio();
    const ctx = audioContext();

    this.ring.resize(Math.ceil(this.seconds * ctx.sampleRate));

    await ctx.audioWorklet.addModule("recorder-worklet.js");
    const stream = await navigator.mediaDevices.getUserMedia(CONSTRAINTS);

    // Permission prompts take as long as the user takes. If stop() ran while we
    // were waiting, release the stream instead of quietly holding the mic open.
    if (generation !== this.generation) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }

    this.stream = stream;
    this.source = ctx.createMediaStreamSource(stream);
    this.node = new AudioWorkletNode(ctx, "recorder", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
    });
    this.node.port.onmessage = (event: MessageEvent<Float32Array>) => this.ring.write(event.data);

    // Some browsers stop pulling on a worklet whose output goes nowhere, so it
    // is connected to a silent sink rather than to the speakers.
    this.sink = ctx.createGain();
    this.sink.gain.value = 0;
    this.source.connect(this.node);
    this.node.connect(this.sink);
    this.sink.connect(ctx.destination);
  }

  stop(): void {
    this.generation++;
    if (this.node) {
      this.node.port.onmessage = null;
      this.node.port.close();
      this.node.disconnect();
    }
    this.source?.disconnect();
    this.sink?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());
    this.node = null;
    this.source = null;
    this.sink = null;
    this.stream = null;
    this.ring.clear();
  }

  /** Copy the most recent `out.length` samples into `out`, oldest first. */
  readLatest(out: Float32Array): boolean {
    return this.ring.readLatest(out);
  }
}
